
import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { LonaIcons } from "@tento-ui/ui/icons";
import { LineChartLegend } from "@tento-ui/components/line-chart/line-chart-legend";
import eyeIcon from "@tento-ui/icons/eye.svg";
import eyeSlashedIcon from "@tento-ui/icons/eye-slashed.svg";
import { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import { SheetLegend } from "@tento-lona/sheets-ui/sheet/sheet-legend.ts";
import { type SheetRow } from "@tento-lona";
import type { Option, RowLocalId, Timescale } from "@tento-lona/sheets";
import { DataAliasPlugin } from "../data.ts";
import type { TextMeasurer } from "@tento-lona/sheets-ui/sheet/default-legend-layout.ts";
import { DataLegendLayout } from "./data-plugin.legend-layout.ts";
import type { SheetLegendClass } from "@tento-lona/sheets-ui/sheet/render-plugin.ts";

/**
 * Width reserved for the embedded `std-line-chart-legend` price axis.
 *
 * Exported because it is also the width of the CONDENSED rail: the axis is
 * right-flush and full height, so a rail exactly this wide keeps it verbatim
 * while everything left of it is clipped. An embedder that collapses to the
 * axis lane (`legendCollapsedWidthPx`) reads this rather than writing 64, so
 * a wider axis moves the lane with it.
 */
export const DATA_AXIS_WIDTH_PX = 64;
/** Must match the canvas's `Y_PADDING_PX` so ticks line up with candles. */
const Y_PADDING_PX = 20;

@component({
  name: "std-data-legend",
})
export class DataLegend extends LonaWebComponent {
  #$legend: SheetLegend.DefaultRow = this.hydrate("legend");
  #$axisLegend: LineChartLegend = this.hydrate("axis-legend");
  #$iconShow: HTMLElement;
  #$iconHide: HTMLElement;

  pluginRef: Option<DataAliasPlugin> = null;
  #rowRef: Option<SheetRow.Joined.Handle> = null;
  #axisOwnerId: Option<RowLocalId> = null;
  #dataHidden = false;

  constructor() {
    super();
    this.#$iconShow = LonaIcons.make(eyeIcon);
    this.#$iconHide = LonaIcons.make(eyeSlashedIcon);
    this.#$iconHide.style.display = "none";
    this.$("toggle").appendChild(this.#$iconShow);
    this.$("toggle").appendChild(this.#$iconHide);

    GESTURE_MANAGER.addPointerEvent(this.$("toggle"), {
      onClick: () => {
        const plugin = this.pluginRef;
        const row = this.#rowRef;
        if (!plugin || !row) return;
        this.#dataHidden = !this.#dataHidden;
        plugin.setHidden(row, this.#dataHidden);
        this.#updateIconState(this.#dataHidden);
      },
    });
  }

  #updateIconState(hidden: boolean): void {
    this.#$iconShow.style.display = hidden ? "none" : "";
    this.#$iconHide.style.display = hidden ? "" : "none";
  }

  bind(row: SheetRow.Joined.Handle, handle: SheetDelegate): void {
    // Self-declaration for the collapsed rail (Sheet's `legend-collapsed`
    // block). A chart row's identity in a narrow lane is its VALUES, and the
    // y-axis below is already right-flush and full height, so it survives a
    // 64px rail untouched; the header (label + eye toggle) is what goes,
    // through the `--legend-row-header-*` tokens in the CSS below. Stated
    // unconditionally — capability belongs to the component, not to the row's
    // data — and re-set on every bind, which is what restores it after
    // pooling (`_recycle` strips every attribute). `SheetLegend.Nested`
    // mirrors it onto the frame the rail's kill rule actually selects.
    this.toggleAttribute("condensed-capable", true);
    this.#rowRef = row;
    this.#axisOwnerId = row.id;
    this.#dataHidden = this.pluginRef?.isHidden(row) ?? false;
    // The eye drives the alias plugin's data-json child visibility; without a
    // DataAliasPlugin (rows-driven chart) it has nothing to act on — hide it.
    // The concrete source row's own legend carries the JSON toggle instead.
    this.$("toggle").style.display = this.pluginRef ? "" : "none";
    this.#$legend.bind(
      row,
      (target) => handle.rebind.revealRowOptionsPopover(row, target),
      handle,
    );
    this.#updateIconState(this.#dataHidden);
    this.#bindAxis();
  }

  /**
   * Render the price y-axis with the shared `std-line-chart-legend`, bound
   * to the chart's `vm` + `yScaler` (the same scaler the canvas paints
   * against, so dragging the axis rescales the candles). The scale is
   * computed by the canvas after its async data load; the canvas re-binds
   * this legend once it's ready (CanvasLayoutPlugin → rebindRowLegend), so a
   * bind before the scale exists simply hides the axis until then.
   */
  #bindAxis(): void {
    const ownerId = this.#axisOwnerId;
    const scale = ownerId
      ? this.pluginRef?.context.store.chartScaleFor(ownerId)
      : null;
    if (!scale) {
      this.#$axisLegend.style.display = "none";
      return;
    }
    this.#$axisLegend.style.display = "block";
    this.#$axisLegend.bind({
      vm: scale.vm,
      yScaler: scale.yScaler,
      yPadding: Y_PADDING_PX,
    });
  }

  static readonly factory: SheetLegendClass<DataLegend> = class {
    static make(): DataLegend {
      return DataLegend.make();
    }

    static bind(
      $legend: DataLegend,
      row: SheetRow.Joined.Handle,
      handle: SheetDelegate,
      _sheetTimescale: Timescale,
    ): void {
      // The legend binds on the data alias row, or — when the alias's data
      // rows are subsumed under a chart canvas — on the visible canvas/layout
      // row whose parent chain reaches the alias. Walk up to the nearest
      // ancestor (self included) owned by a `DataAliasPlugin` so the legend
      // shows the alias label (e.g. "Stocks", not the canvas's default
      // "Canvas") and the show/hide toggle drives the alias's data. Resolve
      // plugins the same way the legend renderer does
      // (`pluginManager.get(row).plugin()`), not via `resolveCellBinding`,
      // whose variant-based lookup misses layout-nested aliases.
      let owner = row;
      let plugin: Option<DataAliasPlugin> = null;
      for (
        let probe: Option<SheetRow.Joined.Handle> = row;
        probe;
        probe = probe.parent()
      ) {
        const candidate = handle.pluginManager.get(probe).plugin();
        if (candidate instanceof DataAliasPlugin) {
          owner = probe;
          plugin = candidate;
          break;
        }
      }
      if (plugin) {
        $legend.pluginRef = plugin;
      }
      $legend.bind(owner, handle);
    }

    static computeWidth(
      row: SheetRow.Joined.Handle,
      measure: TextMeasurer,
    ): number {
      // Mirror `bind`'s owner resolution for width: prefer the nearest
      // alias ancestor's label so the reserved legend width matches the
      // displayed label when bound on a subsuming canvas row. `computeWidth`
      // has no plugin manager, so detect the alias by descriptor role.
      // Reserve the price-axis width alongside the label.
      let label = row.label;
      for (
        let probe: Option<SheetRow.Joined.Handle> = row;
        probe;
        probe = probe.parent()
      ) {
        if (probe.descriptor.role === "alias") {
          label = probe.label ?? label;
          break;
        }
      }
      return DataLegendLayout.width(label ?? "Untitled", measure) +
        DATA_AXIS_WIDTH_PX;
    }
  };

  static $styles: Style.Compat[] = [
    css`
      :host {
        position: relative;
        display: block;
        height: 100%;
      }

      /* Label + show/hide toggle, top-left, clear of the price axis
         (right inset must match DATA_AXIS_WIDTH_PX). */
      #header {
        position: absolute;
        top: 0;
        left: 0;
        right: 64px;
        display: flex;
        align-items: center;
        z-index: 1;
        /* Condensed projection: at the axis lane's width this whole strip is
           the part with nowhere to go — the chart row's own label is usually
           empty and its group row names it anyway, so the header yields the
           lane to the axis. Opacity and hit-testing are two declarations
           because CSS cannot derive one from the other, and the eye toggle
           MUST stop hit-testing: an invisible visibility switch layered over
           the axis would toggle the series on a stray click. Both fall back
           to the expanded value, so nothing changes until the rail publishes
           them. */
        opacity: var(--legend-row-header-opacity, 1);
        pointer-events: var(--legend-row-header-pointer, auto);
      }

      #legend {
        flex: 0 1 auto;
        min-width: 0;
      }

      #toggle {
        display: flex;
        align-items: center;
        padding: 4px 6px;
        cursor: pointer;
        opacity: 0.5;
        color: var(--text-sec);
        flex-shrink: 0;
        border-radius: 4px;
      }

      #toggle:hover {
        opacity: 1;
        background: var(--bg-hover);
      }

      /* Price y-axis: the shared line-chart legend, full height on the
         right so its ticks align with the candles. Deliberately subscribes
         to NOTHING — being right-anchored, it travels with the rail's width
         instead of being clipped by it, which is the whole reason the
         condensed lane is DATA_AXIS_WIDTH_PX wide. This is the mark the collapsed
         chart row keeps. */
      #axis-legend {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        --std-line-chart-legend-bg: transparent;
        /* One amendment to "subscribes to NOTHING": the mark survives at its
           expanded GEOMETRY but not its expanded VOICE. Full-rail tick ink
           (small-ramp, --text-sec) is sized to sit beside a label and a
           toggle; alone in a 64px lane it reads louder than anything else in the
           card. The lane may quiet it — size down the small ramp, ink down
           one text tier — through these two pass-throughs, each falling back
           to the expanded value so an uncondensed rail is untouched. */
        --small-size: var(--legend-row-axis-font, var(--text-9px));
        --std-chart-tick-color: var(--legend-row-axis-color, var(--text-sec));
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <div id="header">
      <std-sheet-legend-default-row id="legend"></std-sheet-legend-default-row>
      <div id="toggle"></div>
    </div>
    <std-line-chart-legend id="axis-legend"></std-line-chart-legend>
  `;
}
